import { useDialog } from '@/context/dialogContext'
import { useAppDispatch } from '@/redux/hooks'
import { logout } from '@/redux/slices/auth'
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

// ✅ 타입 확장: 커스텀 옵션 추가
interface CustomAxiosInternalRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
  _disableLoading?: boolean // 로딩바 비활성화용 커스텀 필드
}

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: Number(process.env.REACT_APP_DEFAULT_TIMEOUT),
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

export const setupAxiosInterceptors = (
  startLoading: () => void,
  stopLoading: () => void,
) => {
  const dispatch = useAppDispatch()
  const { showAlert } = useDialog()

  axiosInstance.interceptors.request.use((config) => {
    const customConfig = config as CustomAxiosInternalRequestConfig

    // ✅ _disableLoading이 아닌 경우에만 로딩 시작
    if (!customConfig._disableLoading) {
      startLoading()
    }

    // API 타임아웃 재설정 (기존 유지)
    axiosInstance.defaults.timeout =
      config.timeout || Number(process.env.REACT_APP_DEFAULT_TIMEOUT)

    return config
  })

  axiosInstance.interceptors.response.use(
    (response) => {
      const customConfig = response.config as CustomAxiosInternalRequestConfig

      // ✅ _disableLoading이 아닌 경우에만 로딩 종료
      if (!customConfig._disableLoading) {
        stopLoading()
      }

      return response
    },
    async (err) => {
      const customConfig = err.config as CustomAxiosInternalRequestConfig

      // ✅ _disableLoading이 아닌 경우에만 로딩 종료
      if (!customConfig?._disableLoading) {
        stopLoading()
      }

      if (!err.response) {
        console.error('Server unreachable:', err.message)
        return Promise.reject({
          code: 'NETWORK_ERROR',
          message: '서버에 연결할 수 없습니다.',
        })
      }

      if (err.response) {
        const status = err.response.status
        const message =
          err.response.data?.message || err.message || 'Unknown error'
        const originalRequest = err.config

        if (status === 401) {
          try {
            const refreshResponse = await axiosInstance.get(
              '/auth/refresh-token',
              {
                // ✅ 리프레시 요청에도 로딩바 생략
                _disableLoading: true,
              } as CustomAxiosInternalRequestConfig,
            )

            if (refreshResponse.data.common.resultCode === 'T0200') {
              return axiosInstance(originalRequest)
            } else {
              dispatch(logout())
              window.location.href = '/'
            }
          } catch (refErr) {
            console.error('Error during refresh token request:', refErr)
            await axios.post('/auth/logout')
            dispatch(logout())
            window.location.href = '/'
          }
        } else {
          console.error(`Error ${status}: ${message}`)
        }
      } else if (err.request) {
        console.error('No response received:', err.request)
      } else {
        console.error('Error setting up request:', err.message)
      }

      return Promise.reject(err)
    },
  )
}

export default axiosInstance
